import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
// import FormControlLabel from "@mui/material/FormControlLabel";
// import Checkbox from "@mui/material/Checkbox";
import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { requestSessionTokenThunk } from "../../modules/session/sessionActions";
import { fetchCurrentUserThunk } from "../../modules/user/userActions";
import SignInValidationScheme from "./SignInValidationScheme";
import { route as homeRoute } from "./../HomeContainer/HomeContainer";
import { route as passwordLostRoute } from "../PasswordLostContainer/PasswordLostContainer";
import hausifyLogo from "../../assets/hausify-logo-app.jpg";

const SignIn = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((v) => !v);
  const handleMouseDownPassword = (event) => {
    // Prevent focus loss
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      emailAddress: "",
      password: "",
      rememberMe: false,
    },
    validationSchema: SignInValidationScheme(intl),
    onSubmit: async (values, { setSubmitting }) => {
      const { emailAddress, password } = values;
      setSubmitting(true);

      try {
        await dispatch(
          requestSessionTokenThunk({ emailAddress, password }),
        ).unwrap();
        await dispatch(fetchCurrentUserThunk()).unwrap();
        history.push(homeRoute);
      } catch (err) {
        // Notifications are handled centrally by rejectionNotificationMiddleware
        console.error("Error while requesting session token.", err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    errors,
    isSubmitting,
  } = formik;
  const formatMessage = intl.formatMessage;

  return (
    <Box
      component="main"
      sx={(theme) => ({
        pt: theme.spacing(6),
        width: "auto",
        display: "block",
        mx: theme.spacing(3),

        [theme.breakpoints.up(400)]: {
          maxWidth: 400,
          ml: "auto",
          mr: "auto",
        },
      })}
    >
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 3,
          gap: 1,
        }}
      >
        <Avatar sx={{ m: 1, width: 64, height: 64 }} src={hausifyLogo} />

        <Typography variant="body1">
          {formatMessage({ id: "subheader.login" })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: "login.helper" })}
        </Typography>
        <Box
          component="form"
          sx={{ width: "100%", mt: 1 }}
          onSubmit={handleSubmit}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="emailAddress"
            name="emailAddress"
            label={formatMessage({ id: "app.email" })}
            type="email"
            value={values.emailAddress}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="email"
            autoFocus
            error={touched.emailAddress && Boolean(errors.emailAddress)}
            helperText={
              touched.emailAddress && errors.emailAddress
                ? errors.emailAddress
                : ""
            }
          />

          <TextField
            margin="normal"
            required
            fullWidth
            id="password"
            name="password"
            label={formatMessage({ id: "app.password" })}
            type={showPassword ? "text" : "password"}
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="current-password"
            error={touched.password && Boolean(errors.password)}
            helperText={
              touched.password && errors.password ? errors.password : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={formatMessage({
                      id: "app.toggle.password.visibility",
                    })}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* <FormControlLabel
            control={
              <Checkbox
                name="rememberMe"
                checked={values.rememberMe}
                onChange={handleChange}
                color="primary"
              />
            }
            label={formatMessage({ id: "app.rememberMe" })}
            sx={{ mt: 1 }}
          /> */}

          <Button
            type="submit"
            size="large"
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            disabled={isSubmitting}
          >
            {formatMessage({ id: "app.login" })}
          </Button>

          <Button
            size="large"
            component={Link}
            to={passwordLostRoute}
            fullWidth
            sx={{ mt: 2 }}
          >
            {formatMessage({ id: "app.forgot.password" })}
          </Button>

          {process.env.REACT_APP_MANAGEMENT_URL ? (
            <>
              <Divider sx={{ my: 2, width: "100%" }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatMessage({ id: "login.admin.helper" })}
              </Typography>
              <Button
                component="a"
                href={process.env.REACT_APP_MANAGEMENT_URL}
                target="_blank"
                rel="noopener"
                fullWidth
                sx={{ mt: 1 }}
                color="inherit"
              >
                {formatMessage({ id: "app.admin.login" })}
              </Button>
            </>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
};

export default SignIn;
